import axios from 'axios';
import store from 'dashboard/store';
import VoiceAPI from './voiceAPIClient';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');
const createCallIncomingEvent = detail => new CustomEvent('call:incoming', { detail });

const normalizeSdp = sdp => {
  if (!sdp) return '';
  const lines = sdp
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);
  return lines.join('\r\n') + '\r\n';
};

class FreeswitchVoiceClient extends EventTarget {
  constructor() {
    super();
    this.ws = null;
    this.peerConnection = null;
    this.localStream = null;
    this.remoteAudioElement = null;
    this.registered = false;
    this.activeCall = null;
    this.credentials = null;
    this.pingInterval = null;
    this.cseq = 1;
    this.callId = null;
    this.fromTag = null;
    this.initPromise = null;
    this.isDirectUdpWorking = null;
    this.udpCheckPromise = null;
    this.contactHost = null;
  }

  ensureAudioElement() {
    if (!this.remoteAudioElement && typeof document !== 'undefined') {
      let audio = document.getElementById('freeswitch-remote-audio');
      if (!audio) {
        audio = document.createElement('audio');
        audio.id = 'freeswitch-remote-audio';
        audio.autoplay = true;
        audio.style.display = 'none';
        document.body.appendChild(audio);
      }
      this.remoteAudioElement = audio;
    }
    return this.remoteAudioElement;
  }

  async checkDirectUdpConnectivity(timeoutMs = 1200) {
    if (this.isDirectUdpWorking !== null) {
      return this.isDirectUdpWorking;
    }
    if (this.udpCheckPromise) {
      return this.udpCheckPromise;
    }

    this.udpCheckPromise = new Promise((resolve) => {
      let pc = null;
      let hasResolved = false;
      let timer = null;

      const finish = (result) => {
        if (hasResolved) return;
        hasResolved = true;
        if (timer) clearTimeout(timer);
        this.isDirectUdpWorking = result;
        this.udpCheckPromise = null;
        console.log(
          `FreeSWITCH WebRTC (Strategy B): Direct UDP connectivity check: ${
            result
              ? 'PASSED (Direct UDP / STUN active)'
              : 'BLOCKED / STRICT NAT (Forcing TURN over TCP relay)'
          }`
        );
        if (pc) {
          try {
            pc.close();
          } catch (e) {}
        }
        resolve(result);
      };

      try {
        const stunServers = [
          { urls: ['stun:20.46.147.126:3478', 'stun:stun.l.google.com:19302'] },
        ];
        pc = new RTCPeerConnection({ iceServers: stunServers });

        pc.onicecandidate = (event) => {
          if (
            event.candidate &&
            (event.candidate.type === 'srflx' || event.candidate.candidate?.includes('typ srflx'))
          ) {
            finish(true);
          } else if (!event.candidate && !hasResolved) {
            finish(false);
          }
        };

        pc.createDataChannel('ping');
        pc.createOffer()
          .then((offer) => pc.setLocalDescription(offer))
          .catch(() => finish(false));

        timer = setTimeout(() => {
          finish(false);
        }, timeoutMs);
      } catch (err) {
        console.warn('FreeSWITCH: Direct UDP connectivity test error:', err);
        finish(false);
      }
    });

    return this.udpCheckPromise;
  }

  getPeerConnectionConfig() {
    const stunServers = [
      { urls: ['stun:20.46.147.126:3478', 'stun:stun.l.google.com:19302'] },
    ];

    const turnUsername =
      this.credentials?.iceServers?.find((s) => s.username)?.username || 'webrtcuser';
    const turnCredential =
      this.credentials?.iceServers?.find((s) => s.credential)?.credential || 'webrtcpassword123';

    const tcpRelayServers = [
      {
        urls: ['turn:20.46.147.126:3478?transport=tcp'],
        username: turnUsername,
        credential: turnCredential,
      },
      {
        urls: ['turns:20.46.147.126:5349?transport=tcp'],
        username: turnUsername,
        credential: turnCredential,
      },
    ];

    const defaultIceServers = [
      ...stunServers,
      ...tcpRelayServers,
      {
        urls: ['turn:20.46.147.126:3478?transport=udp'],
        username: turnUsername,
        credential: turnCredential,
      },
    ];

    let iceServers;
    let iceTransportPolicy;

    if (this.isDirectUdpWorking === true) {
      // 1. When Ports are Open: Direct STUN Only — No Coturn Relay
      // What browser sends in SDP: Only host and srflx candidates
      iceServers = stunServers;
      iceTransportPolicy = 'all';
      console.log(
        'FreeSWITCH WebRTC: Using DIRECT STUN ONLY configuration (iceTransportPolicy=all, STUN only)'
      );
    } else if (this.isDirectUdpWorking === false) {
      // 2. When Ports are Blocked / Strict NAT: TURN TCP Relay Only
      // Forces instant TCP tunneling without wasting time on dead UDP candidates
      iceServers = tcpRelayServers;
      iceTransportPolicy = 'relay';
      console.log(
        'FreeSWITCH WebRTC: Using TURN TCP RELAY ONLY configuration (iceTransportPolicy=relay, TCP relay only)'
      );
    } else {
      iceServers =
        this.credentials?.iceServers && this.credentials.iceServers.length > 0
          ? this.credentials.iceServers
          : defaultIceServers;
      iceTransportPolicy = 'all';
    }

    return {
      iceServers,
      iceTransportPolicy,
      bundlePolicy: 'balanced',
    };
  }

  async resolveCurrentUserId(maxWaitMs = 6000) {
    let userId =
      store?.getters?.getCurrentUserID ||
      store?.getters?.getCurrentUser?.id ||
      window.currentUser?.id ||
      window.chatspaceConfig?.user?.id ||
      window.chatspaceConfig?.current_user_id ||
      window.chatwootConfig?.user?.id ||
      window.chatwootConfig?.current_user_id;

    if (userId) return userId;

    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, 200));
      userId =
        store?.getters?.getCurrentUserID ||
        store?.getters?.getCurrentUser?.id ||
        window.currentUser?.id ||
        window.chatspaceConfig?.user?.id ||
        window.chatspaceConfig?.current_user_id ||
        window.chatwootConfig?.user?.id ||
        window.chatwootConfig?.current_user_id;
      if (userId) return userId;
    }

    return null;
  }

  async fetchCredentials(inboxId = null, retryCount = 0) {
    try {
      if (inboxId) {
        try {
          const res = await VoiceAPI.getToken(inboxId, { provider: 'freeswitch' });
          if (res?.sip_username && res?.sip_password) {
            return {
              username: res.sip_username,
              password: res.sip_password,
              domain: res.sip_domain || 'media.justcreo.com',
              wsServer: res.ws_server || 'wss://media.justcreo.com:7443',
              iceServers: res.ice_servers || [],
            };
          }
        } catch (tokenErr) {
          console.warn('VoiceAPI getToken notice:', tokenErr);
        }
      }

      const currentUserId = await this.resolveCurrentUserId();
      if (!currentUserId) {
        console.warn('FreeSWITCH: User session not ready yet.');
        return null;
      }

      const res = await axios.post('/api/v1/telephony/directory', {
        user: `agent_${currentUserId}`,
      });

      if (res?.data?.username) {
        const data = res.data;
        return {
          username: data.username,
          password: data.password,
          domain: data.domain || 'media.justcreo.com',
          wsServer: data.ws_server || 'wss://media.justcreo.com:7443',
          callerId: data.caller_id || data.callerId || null,
          accountId: data.account_id || data.accountId || null,
        };
      }
    } catch (err) {
      if (retryCount < 3) {
        await new Promise(resolve => setTimeout(resolve, 800 * (retryCount + 1)));
        return this.fetchCredentials(inboxId, retryCount + 1);
      }
      console.warn('FreeSWITCH credentials fetch warning:', err);
    }

    return null;
  }

  getWebSocketUrl() {
    if (typeof window !== 'undefined' && window.location && window.location.host) {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${window.location.host}/freeswitch-ws`;
    }
    return this.credentials?.wsServer || 'wss://media.justcreo.com:7443';
  }

  async initialize(inboxId = null) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return true;
    }
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        this.credentials = await this.fetchCredentials(inboxId);
        if (!this.credentials || !this.credentials.username) {
          console.warn('FreeSWITCH: No agent credentials found, skipping WebRTC registration.');
          return false;
        }
        this.ensureAudioElement();

        const proxiedUrl = this.getWebSocketUrl();
        const directUrl = this.credentials?.wsServer || 'wss://media.justcreo.com:7443';
        const endpoints = [proxiedUrl];
        if (directUrl && directUrl !== proxiedUrl) {
          endpoints.push(directUrl);
        }

        const connectToEndpoint = (url) => {
          return new Promise((resolve) => {
            console.log(`FreeSWITCH: Connecting SIP WebRTC client to ${url}...`);
            if (this.ws) {
              try {
                this.ws.close();
              } catch (e) {}
            }
            try {
              this.ws = new WebSocket(url, 'sip');
            } catch (err) {
              console.warn(`FreeSWITCH: Failed to construct WebSocket for ${url}:`, err);
              resolve(false);
              return;
            }

            const timeout = setTimeout(() => {
              console.warn(`FreeSWITCH: WebSocket connection to ${url} timed out after 6s`);
              resolve(false);
            }, 6000);

            this.ws.onopen = () => {
              clearTimeout(timeout);
              console.log(`FreeSWITCH: WebSocket connected to ${url}, sending REGISTER...`);
              this.sendRegister();
              this.startPing();
              // Strategy B: Run direct UDP STUN connectivity check in background immediately on connect
              this.checkDirectUdpConnectivity().catch(() => {});
              resolve(true);
            };

            this.ws.onmessage = (event) => {
              this.handleSipMessage(event.data);
            };

            this.ws.onclose = (e) => {
              this.registered = false;
              this.stopPing();
              console.warn(`FreeSWITCH: WebSocket closed (${url}): code=${e.code} reason=${e.reason}`);
            };

            this.ws.onerror = (err) => {
              clearTimeout(timeout);
              console.warn(`FreeSWITCH: WebSocket error (${url}):`, err);
              resolve(false);
            };
          });
        };

        let connected = false;
        for (const url of endpoints) {
          // eslint-disable-next-line no-await-in-loop
          connected = await connectToEndpoint(url);
          if (connected) break;
        }

        return connected;
      } catch (e) {
        console.error('FreeSWITCH init error:', e);
        return false;
      } finally {
        this.initPromise = null;
      }
    })();


    return this.initPromise;
  }

  sendRegister() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.credentials) return;
    const { username, domain } = this.credentials;
    const callId = this.generateId(16);
    const branch = `z9hG4bK${this.generateId(12)}`;
    const tag = this.generateId(10);
    this.fromTag = tag;
    this.callId = callId;

    if (!this.contactHost) {
      this.contactHost = `${this.generateId(8)}.invalid`;
    }
    const contactHost = this.contactHost;
    const sipRegister =
      `REGISTER sip:${domain} SIP/2.0\r\n` +
      `Via: SIP/2.0/WSS ${contactHost};branch=${branch};rport\r\n` +
      `Max-Forwards: 70\r\n` +
      `From: <sip:${username}@${domain}>;tag=${tag}\r\n` +
      `To: <sip:${username}@${domain}>\r\n` +
      `Call-ID: ${callId}\r\n` +
      `CSeq: ${this.cseq++} REGISTER\r\n` +
      `Contact: <sip:${username}@${contactHost};transport=ws>;expires=3600\r\n` +
      `Allow: INVITE, ACK, CANCEL, BYE, NOTIFY, REFER, OPTIONS, INFO, SUBSCRIBE\r\n` +
      `Supported: replaces\r\n` +
      `User-Agent: omni-WebRTC/1.0\r\n` +
      `Content-Length: 0\r\n\r\n`;

    this.ws.send(sipRegister);
  }

  startPing() {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('\r\n\r\n');
      }
    }, 25000);
  }

  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  parseSipHeaders(headerText) {
    const headers = {};
    const viaLines = [];
    const lines = headerText.split(/\r?\n/);
    lines.forEach((line) => {
      if (line.toLowerCase().startsWith('via:') || line.toLowerCase().startsWith('v:')) {
        viaLines.push(line.substring(line.indexOf(':') + 1).trim());
      } else {
        const colonIdx = line.indexOf(':');
        if (colonIdx > 0) {
          const key = line.substring(0, colonIdx).trim().toLowerCase();
          const val = line.substring(colonIdx + 1).trim();
          headers[key] = val;
        }
      }
    });
    return { headers, viaLines };
  }

  handleSipMessage(msg) {
    if (typeof msg !== 'string') return;

    const trimmedMsg = msg.trimStart();
    const cseqMatch = msg.match(/CSeq:\s*\d+\s+([A-Z]+)/i);
    const cseqMethod = cseqMatch ? cseqMatch[1].toUpperCase() : '';

    if (trimmedMsg.startsWith('SIP/2.0 200 OK')) {
      if (cseqMethod === 'REGISTER') {
        this.registered = true;
        console.log(`FreeSWITCH: Registered successfully as ${this.credentials?.username}@${this.credentials?.domain}`);
      } else if (cseqMethod === 'INVITE') {
        this.handleCallAnswered(msg);
      } else if (cseqMethod === 'CANCEL' || cseqMethod === 'BYE') {
        console.log(`FreeSWITCH: Received 200 OK for ${cseqMethod}`);
      }
    } else if (trimmedMsg.startsWith('SIP/2.0 180') || trimmedMsg.startsWith('SIP/2.0 183')) {
      this.handleRingingOrProgress(msg);
    } else if (trimmedMsg.startsWith('SIP/2.0 4') || trimmedMsg.startsWith('SIP/2.0 5') || trimmedMsg.startsWith('SIP/2.0 6')) {
      this.handleCallError(msg);
    } else if (trimmedMsg.startsWith('INVITE')) {
      this.handleIncomingInvite(msg);
    } else if (trimmedMsg.startsWith('ACK')) {
      console.log('FreeSWITCH: Call ACK received from FreeSWITCH - Media stream active!');
    } else if (trimmedMsg.startsWith('BYE') || (cseqMethod === 'BYE' && !trimmedMsg.startsWith('SIP/2.0'))) {
      console.log('FreeSWITCH: Received BYE');
      this.handleHangup(msg);
    } else if (trimmedMsg.startsWith('CANCEL')) {
      console.log('FreeSWITCH: Received CANCEL');
      if (this.activeCall) {
        this.sendSipResponse(200, 'OK', '', msg);
        this.sendSipResponse(487, 'Request Terminated', '', msg);
      }
      this.endClientCall(false);
    }
  }

  async handleRingingOrProgress(sipMessage) {
    console.log('FreeSWITCH: Received Ringing/Progress response:\n', sipMessage);
    let splitIndex = sipMessage.indexOf('\r\n\r\n');
    let delimiterLength = 4;
    if (splitIndex === -1) {
      splitIndex = sipMessage.indexOf('\n\n');
      delimiterLength = 2;
    }
    const headerPart = splitIndex !== -1 ? sipMessage.substring(0, splitIndex) : sipMessage;
    const sdpBody = splitIndex !== -1 ? sipMessage.substring(splitIndex + delimiterLength) : '';
    const { headers } = this.parseSipHeaders(headerPart);
    const toVal = headers['to'] || headers['t'] || '';
    const toTagMatch = toVal.match(/tag=([^;]+)/);
    if (toTagMatch && this.activeCall) {
      this.activeCall.toTag = toTagMatch[1];
    }
    if (headers['contact'] && this.activeCall) {
      this.activeCall.contactHeader = headers['contact'];
    }
    if (headers['record-route'] && this.activeCall) {
      this.activeCall.recordRoute = headers['record-route'];
    }
    if (this.activeCall) {
      this.activeCall.state = 'ringing';
    }
    if (sdpBody && this.peerConnection && this.peerConnection.signalingState === 'have-local-offer') {
      try {
        console.log('FreeSWITCH: Applying early media SDP answer (ringback audio)...');
        await this.peerConnection.setRemoteDescription(
          new RTCSessionDescription({ type: 'pranswer', sdp: normalizeSdp(sdpBody) })
        );
        console.log('FreeSWITCH: Early media audio connected successfully!');
      } catch (e) {
        console.warn('FreeSWITCH early media SDP warning:', e);
      }
    }
    this.dispatchEvent(new CustomEvent('call:ringing', { detail: this.activeCall }));
  }

  async handleCallAnswered(sipMessage) {
    console.log('FreeSWITCH: Outbound call 200 OK answered by FreeSWITCH:\n', sipMessage);
    let splitIndex = sipMessage.indexOf('\r\n\r\n');
    let delimiterLength = 4;
    if (splitIndex === -1) {
      splitIndex = sipMessage.indexOf('\n\n');
      delimiterLength = 2;
    }
    const headerPart = splitIndex !== -1 ? sipMessage.substring(0, splitIndex) : sipMessage;
    const sdpBody = splitIndex !== -1 ? sipMessage.substring(splitIndex + delimiterLength) : '';
    const { headers } = this.parseSipHeaders(headerPart);
    const toVal = headers['to'] || headers['t'] || '';
    const toTagMatch = toVal.match(/tag=([^;]+)/);
    if (this.activeCall) {
      this.activeCall.answered = true;
      this.activeCall.state = 'connected';
      if (toTagMatch) {
        this.activeCall.toTag = toTagMatch[1];
      }
      if (headers['contact']) {
        this.activeCall.contactHeader = headers['contact'];
      }
      if (headers['record-route']) {
        this.activeCall.recordRoute = headers['record-route'];
      }
    }

    if (
      sdpBody &&
      this.peerConnection &&
      (this.peerConnection.signalingState === 'have-local-offer' ||
        this.peerConnection.signalingState === 'have-local-pranswer')
    ) {
      try {
        await this.peerConnection.setRemoteDescription(
          new RTCSessionDescription({ type: 'answer', sdp: normalizeSdp(sdpBody) })
        );
      } catch (e) {
        console.warn('FreeSWITCH answer SDP warning:', e);
      }
    }


    // Send SIP ACK for 200 OK (RFC 3261 Section 13.2.2.4: new branch, CSeq matching INVITE, Request-URI from Contact)
    if (this.activeCall && this.ws && this.ws.readyState === WebSocket.OPEN) {
      const username = this.credentials?.username || this.activeCall?.fromHeader?.match(/<sip:([^@>]+)/)?.[1] || 'agent';
      const domain = this.credentials?.domain || 'media.justcreo.com';
      const dest = this.activeCall.destinationNumber || this.activeCall.caller;
      const cseqHeader = headers['cseq'] || headers['cs'] || '';
      const cseqMatch = cseqHeader.match(/^(\d+)/);
      const cseqNumber = cseqMatch ? cseqMatch[1] : (this.activeCall.cseq || this.cseq || 1);
      const ackBranch = `z9hG4bK${this.generateId(12)}`;

      let ackRequestUri = `sip:${dest}@${domain}`;
      if (headers['contact']) {
        const contactMatch = headers['contact'].match(/<([^>]+)>/);
        if (contactMatch) {
          ackRequestUri = contactMatch[1];
        }
      }

      const routeHeader = headers['record-route'] ? `Route: ${headers['record-route']}\r\n` : '';

      let toHeaderFormatted = toVal || this.activeCall.toHeader || `<sip:${dest}@${domain}>`;
      if (this.activeCall.toTag && !toHeaderFormatted.includes('tag=')) {
        toHeaderFormatted = `${toHeaderFormatted};tag=${this.activeCall.toTag}`;
      }

      let fromHeaderFormatted = headers['from'] || headers['f'] || this.activeCall.fromHeader || `<sip:${username}@${domain}>`;
      if (this.activeCall.fromTag && !fromHeaderFormatted.includes('tag=')) {
        fromHeaderFormatted = `${fromHeaderFormatted};tag=${this.activeCall.fromTag}`;
      }

      const callIdHeader = headers['call-id'] || headers['i'] || this.activeCall.callSid;

      const contactHost = this.contactHost || this.activeCall?.contactHost || `${this.generateId(8)}.invalid`;
      const ack =
        `ACK ${ackRequestUri} SIP/2.0\r\n` +
        `Via: SIP/2.0/WSS ${contactHost};branch=${ackBranch};rport\r\n` +
        `Max-Forwards: 70\r\n` +
        routeHeader +
        `From: ${fromHeaderFormatted}\r\n` +
        `To: ${toHeaderFormatted}\r\n` +
        `Call-ID: ${callIdHeader}\r\n` +
        `CSeq: ${cseqNumber} ACK\r\n` +
        `Contact: <sip:${username}@${contactHost};transport=ws>\r\n` +
        `User-Agent: omni-WebRTC/1.0\r\n` +
        `Content-Length: 0\r\n\r\n`;

      console.log('FreeSWITCH: Sending SIP ACK for 200 OK:\n', ack);
      this.ws.send(ack);
    }


    this.dispatchEvent(new CustomEvent('call:connected', { detail: this.activeCall }));
  }

  handleCallError(sipMessage) {
    console.warn('FreeSWITCH: Call error response received:\n', sipMessage);
    const splitIndex = sipMessage.indexOf('\r\n\r\n');
    const headerPart = splitIndex !== -1 ? sipMessage.substring(0, splitIndex) : sipMessage;
    const { headers } = this.parseSipHeaders(headerPart);
    const toVal = headers['to'] || headers['t'] || '';
    const cseqVal = headers['cseq'] || headers['cs'] || '1 INVITE';

    const call = this.activeCall || this.lastEndedCall;
    if (call && call.callDirection === 'outbound') {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const username = this.credentials?.username || call?.fromHeader?.match(/<sip:([^@>]+)/)?.[1] || 'agent';
        const domain = this.credentials?.domain || 'media.justcreo.com';
        const dest = call.destinationNumber || call.caller;
        const cseqParts = cseqVal.split(' ');
        const cseqNumber = cseqParts[0];
        const contactHost = call.contactHost || `${this.generateId(8)}.invalid`;
        const ackBranch = call.branch || `z9hG4bK${this.generateId(12)}`;
        const ack =
          `ACK sip:${dest}@${domain} SIP/2.0\r\n` +
          `Via: SIP/2.0/WSS ${contactHost};branch=${ackBranch};rport\r\n` +
          `Max-Forwards: 70\r\n` +
          `From: ${call.fromHeader || `<sip:${username}@${domain}>;tag=${call.fromTag}`}\r\n` +
          `To: ${toVal || `<sip:${dest}@${domain}>`}\r\n` +
          `Call-ID: ${call.callSid}\r\n` +
          `CSeq: ${cseqNumber} ACK\r\n` +
          `Contact: <sip:${username}@${contactHost};transport=ws>\r\n` +
          `User-Agent: omni-WebRTC/1.0\r\n` +
          `Content-Length: 0\r\n\r\n`;
        console.log('FreeSWITCH: Sending SIP ACK for error response:\n', ack);
        this.ws.send(ack);
      }
      this.endClientCall(false);
    }
  }

  handleIncomingInvite(sipMessage) {
    console.log('FreeSWITCH: Received incoming INVITE from FreeSWITCH:\n', sipMessage);
    const splitIndex = sipMessage.indexOf('\r\n\r\n');
    let headerPart = '';
    let sdpOffer = '';

    if (splitIndex !== -1) {
      headerPart = sipMessage.substring(0, splitIndex);
      sdpOffer = sipMessage.substring(splitIndex + 4);
    } else {
      const splitLfIndex = sipMessage.indexOf('\n\n');
      if (splitLfIndex !== -1) {
        headerPart = sipMessage.substring(0, splitLfIndex);
        sdpOffer = sipMessage.substring(splitLfIndex + 2);
      } else {
        headerPart = sipMessage;
      }
    }

    const { headers, viaLines } = this.parseSipHeaders(headerPart);
    const callSid = headers['call-id'] || headers['i'] || this.generateId(16);
    const fromVal = headers['from'] || headers['f'] || '';
    const toVal = headers['to'] || headers['t'] || '';
    const contactVal = headers['contact'] || headers['m'] || '';
    const recordRouteVal = headers['record-route'] || '';
    const cseqVal = headers['cseq'] || '1 INVITE';
    const callerMatch = fromVal.match(/"([^"]+)"|<sip:([^@>]+)|sip:([^@;>]+)/);
    const callerNumber = callerMatch ? (callerMatch[1] || callerMatch[2] || callerMatch[3]) : 'Customer';
    const inboxId = headers['x-omni-inbox-id'] ? parseInt(headers['x-omni-inbox-id'], 10) : undefined;
    const accountId = headers['x-omni-account-id'] ? parseInt(headers['x-omni-account-id'], 10) : undefined;
    const destination = headers['x-omni-destination'] || undefined;

    const toHostMatch = toVal.match(/@([^;>]+)/);
    const incomingContactHost = toHostMatch ? toHostMatch[1] : (this.contactHost || `${this.generateId(8)}.invalid`);

    this.activeCall = {
      callSid,
      caller: callerNumber,
      inboxId,
      accountId,
      destination,
      fromHeader: fromVal,
      toHeader: toVal,
      contactHost: incomingContactHost,
      contactHeader: contactVal,
      recordRoute: recordRouteVal,
      viaLines: viaLines,
      cseq: cseqVal,
      sdpOffer: normalizeSdp(sdpOffer),
      rawInvite: sipMessage,
      toTag: `tag_${this.generateId(8)}`,
      callDirection: 'inbound',
      startTime: Date.now(),
    };

    // 1. Send 100 Trying & 180 Ringing back to FreeSWITCH
    this.sendSipResponse(100, 'Trying');
    this.sendSipResponse(180, 'Ringing');

    // 2. Emit call:incoming event to UI (triggering the ringing popup & ringtone)
    this.dispatchEvent(createCallIncomingEvent(this.activeCall));
  }

  sendSipResponse(statusCode, reason, sdpBody = '', requestMessage = null) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    let fromHeader = this.activeCall?.fromHeader;
    let toHeader = this.activeCall?.toHeader;
    let viaLines = this.activeCall?.viaLines || [];
    let callSid = this.activeCall?.callSid;
    let cseq = this.activeCall?.cseq;
    let toTag = this.activeCall?.toTag;

    if (requestMessage) {
      let splitIndex = requestMessage.indexOf('\r\n\r\n');
      if (splitIndex === -1) splitIndex = requestMessage.indexOf('\n\n');
      const headerPart = splitIndex !== -1 ? requestMessage.substring(0, splitIndex) : requestMessage;
      const parsed = this.parseSipHeaders(headerPart);
      if (parsed.headers['from']) fromHeader = parsed.headers['from'];
      if (parsed.headers['to']) toHeader = parsed.headers['to'];
      if (parsed.headers['call-id'] || parsed.headers['i']) callSid = parsed.headers['call-id'] || parsed.headers['i'];
      if (parsed.headers['cseq']) cseq = parsed.headers['cseq'];
      if (parsed.viaLines && parsed.viaLines.length) viaLines = parsed.viaLines;
    }

    const username = this.credentials?.username || this.activeCall?.fromHeader?.match(/<sip:([^@>]+)/)?.[1] || 'agent';
    const domain = this.credentials?.domain || 'media.justcreo.com';

    let toFormatted = toHeader || `<sip:${username}@${domain}>`;
    if (statusCode > 100 && toTag && !toFormatted.includes('tag=')) {
      toFormatted = `${toFormatted};tag=${toTag}`;
    }
    const viaBlock = viaLines && viaLines.length ? viaLines.map(v => `Via: ${v}`).join('\r\n') : '';

    const contactHost = this.activeCall?.contactHost || this.contactHost || `${this.generateId(8)}.invalid`;
    let response =
      `SIP/2.0 ${statusCode} ${reason}\r\n` +
      (viaBlock ? `${viaBlock}\r\n` : '') +
      `From: ${fromHeader || `<sip:${username}@${domain}>`}\r\n` +
      `To: ${toFormatted}\r\n` +
      `Call-ID: ${callSid || ''}\r\n` +
      `CSeq: ${cseq || '1'}\r\n` +
      `Contact: <sip:${username}@${contactHost};transport=ws>\r\n` +
      `Allow: INVITE, ACK, CANCEL, BYE, NOTIFY, REFER, OPTIONS, INFO, SUBSCRIBE\r\n` +
      `Supported: replaces\r\n` +
      `User-Agent: omni-WebRTC/1.0\r\n`;

    if (sdpBody) {
      const normalizedSdp = normalizeSdp(sdpBody);
      const sdpBytes = new TextEncoder().encode(normalizedSdp).length;
      response +=
        `Content-Type: application/sdp\r\n` +
        `Content-Length: ${sdpBytes}\r\n\r\n` +
        normalizedSdp;
    } else {
      response += `Content-Length: 0\r\n\r\n`;
    }

    console.log(`FreeSWITCH: Sending SIP ${statusCode} ${reason} over WebSocket:\n`, response);
    this.ws.send(response);
  }

  rejectCall(statusCode = 486, reason = 'Busy Here') {
    if (this.activeCall) {
      if (this.activeCall.callDirection === 'outbound') {
        this.endClientCall(true);
        return;
      }
      this.sendSipResponse(statusCode, reason);
    }
    this.endClientCall(false);
  }

  async acceptCall() {
    if (!this.activeCall || !this.activeCall.sdpOffer) {
      console.warn('FreeSWITCH: Cannot accept call - no active invite or SDP offer');
      return null;
    }

    try {
      console.log('FreeSWITCH: Requesting microphone permission...');
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Ensure direct UDP connectivity check completes (or timeout gracefully to default)
      await this.checkDirectUdpConnectivity(1000);

      const rtcConfig = this.getPeerConnectionConfig();
      console.log('FreeSWITCH: Creating RTCPeerConnection (acceptCall) with config:', rtcConfig);
      this.peerConnection = new RTCPeerConnection(rtcConfig);

      this.peerConnection.onconnectionstatechange = () => {
        const state = this.peerConnection?.connectionState;
        console.log('FreeSWITCH: PeerConnection connectionState:', state);
        if (state === 'failed' || state === 'closed') {
          console.log('FreeSWITCH: WebRTC connection failed/closed -> ending call');
          this.endClientCall(false);
        }
      };

      let inboundIceDisconnectTimer = null;
      this.peerConnection.oniceconnectionstatechange = () => {
        const iceState = this.peerConnection?.iceConnectionState;
        console.log('FreeSWITCH: PeerConnection iceConnectionState:', iceState);
        if (iceState === 'connected' || iceState === 'completed') {
          if (inboundIceDisconnectTimer) {
            clearTimeout(inboundIceDisconnectTimer);
            inboundIceDisconnectTimer = null;
          }
        } else if (iceState === 'disconnected') {
          console.log('FreeSWITCH: WebRTC ICE temporarily disconnected, waiting for reconnection...');
          if (!inboundIceDisconnectTimer) {
            inboundIceDisconnectTimer = setTimeout(() => {
              if (this.peerConnection?.iceConnectionState === 'disconnected') {
                console.log('FreeSWITCH: WebRTC ICE disconnected timeout -> ending call');
                this.endClientCall(false);
              }
            }, 8000);
          }
        } else if (iceState === 'failed' || iceState === 'closed') {
          if (inboundIceDisconnectTimer) clearTimeout(inboundIceDisconnectTimer);
          console.log('FreeSWITCH: WebRTC ICE failed/closed -> ending call');
          this.endClientCall(false);
        }
      };

      this.localStream.getTracks().forEach((track) => {
        this.peerConnection.addTrack(track, this.localStream);
      });

      this.peerConnection.ontrack = (event) => {
        console.log('FreeSWITCH: Remote audio track received!', event);
        const audio = this.ensureAudioElement();
        if (audio) {
          if (event.streams && event.streams[0]) {
            audio.srcObject = event.streams[0];
          } else if (event.track) {
            audio.srcObject = new MediaStream([event.track]);
          }
          audio.play().catch((e) => console.warn('Audio autoplay notice:', e));
        }
      };

      console.log('FreeSWITCH: Setting remote description (SDP Offer)...');
      const normalizedOffer = normalizeSdp(this.activeCall.sdpOffer);
      await this.peerConnection.setRemoteDescription(
        new RTCSessionDescription({ type: 'offer', sdp: normalizedOffer })
      );

      console.log('FreeSWITCH: Creating WebRTC Answer...');
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      await new Promise((resolve) => {
        if (this.peerConnection.iceGatheringState === 'complete') {
          resolve();
          return;
        }
        let timer = null;
        let hasRelayCandidate = false;

        const onIceCandidate = (e) => {
          if (e.candidate && (e.candidate.type === 'relay' || e.candidate.candidate?.includes('typ relay'))) {
            hasRelayCandidate = true;
          }
        };

        const onGather = () => {
          if (this.peerConnection.iceGatheringState === 'complete') {
            cleanup();
            resolve();
          }
        };

        const cleanup = () => {
          if (timer) clearTimeout(timer);
          this.peerConnection.removeEventListener('icegatheringstatechange', onGather);
          this.peerConnection.removeEventListener('icecandidate', onIceCandidate);
        };

        this.peerConnection.addEventListener('icegatheringstatechange', onGather);
        this.peerConnection.addEventListener('icecandidate', onIceCandidate);

        // Allow up to 2000ms for TURN relay candidates to gather on restrictive Wi-Fi
        timer = setTimeout(() => {
          cleanup();
          resolve();
        }, 2000);
      });

      const finalAnswerSdp = normalizeSdp(this.peerConnection.localDescription?.sdp || answer.sdp);
      console.log('FreeSWITCH: Sending SIP 200 OK with SDP answer to FreeSWITCH...');
      this.sendSipResponse(200, 'OK', finalAnswerSdp);

      return this.activeCall;
    } catch (err) {
      console.error('FreeSWITCH accept call error:', err);
      this.endClientCall(false);
      throw err;
    }
  }

  async makeCall(destinationNumber, meta = {}) {
    console.log('FreeSWITCH: makeCall called for:', destinationNumber, meta);
    if (!destinationNumber) {
      throw new Error('Destination phone number required');
    }

    const connected = await this.initialize(meta.inboxId);
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Failed to connect to FreeSWITCH WebRTC server (wss://media.justcreo.com:7443).');
    }
    if (!this.credentials || !this.credentials.username) {
      throw new Error('No FreeSWITCH agent credentials available');
    }

    this.ensureAudioElement();

    const normalizedDest = destinationNumber.replace(/[\s\-()]/g, '');
    const { username, domain } = this.credentials;
    const callSid = `${this.generateId(16)}-${Date.now()}`;
    const fromTag = this.generateId(10);
    const branch = `z9hG4bK${this.generateId(12)}`;

    console.log(`FreeSWITCH: Requesting mic for outbound call to ${normalizedDest}...`);
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (micErr) {
      console.error('FreeSWITCH: Microphone error:', micErr);
      throw new Error(`Microphone access failed: ${micErr.message}`);
    }

    // Ensure direct UDP connectivity check completes (or timeout gracefully to default)
    await this.checkDirectUdpConnectivity(1000);

    const rtcConfig = this.getPeerConnectionConfig();
    console.log('FreeSWITCH: Creating RTCPeerConnection (makeCall) with config:', rtcConfig);
    this.peerConnection = new RTCPeerConnection(rtcConfig);

    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection?.connectionState;
      console.log('FreeSWITCH: RTCPeerConnection state:', state);
      if (state === 'failed' || state === 'closed') {
        console.log('FreeSWITCH: WebRTC connection failed/closed -> ending call');
        this.endClientCall(false);
      }
    };

    let iceDisconnectTimer = null;
    this.peerConnection.oniceconnectionstatechange = () => {
      const iceState = this.peerConnection?.iceConnectionState;
      console.log('FreeSWITCH: ICE connection state:', iceState);
      if (iceState === 'connected' || iceState === 'completed') {
        if (iceDisconnectTimer) {
          clearTimeout(iceDisconnectTimer);
          iceDisconnectTimer = null;
        }
      } else if (iceState === 'disconnected') {
        // 'disconnected' is transient during SDP renegotiations (183 Session Progress -> 200 OK)
        console.log('FreeSWITCH: WebRTC ICE temporarily disconnected, waiting for reconnection...');
        if (!iceDisconnectTimer) {
          iceDisconnectTimer = setTimeout(() => {
            if (this.peerConnection?.iceConnectionState === 'disconnected') {
              console.log('FreeSWITCH: WebRTC ICE disconnected timeout -> ending call');
              this.endClientCall(false);
            }
          }, 8000);
        }
      } else if (iceState === 'failed' || iceState === 'closed') {
        if (iceDisconnectTimer) clearTimeout(iceDisconnectTimer);
        console.log('FreeSWITCH: WebRTC ICE failed/closed -> ending call');
        this.endClientCall(false);
      }
    };

    this.localStream.getTracks().forEach((track) => {
      this.peerConnection.addTrack(track, this.localStream);
    });

    this.peerConnection.ontrack = (event) => {
      console.log('FreeSWITCH: Remote audio received for outbound call:', event);
      const audio = this.ensureAudioElement();
      if (audio) {
        if (event.streams && event.streams[0]) {
          audio.srcObject = event.streams[0];
        } else if (event.track) {
          audio.srcObject = new MediaStream([event.track]);
        }
        audio.play().catch((e) => console.warn('Audio autoplay notice:', e));
      }
    };

    const offer = await this.peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });
    await this.peerConnection.setLocalDescription(offer);

    await new Promise((resolve) => {
      if (this.peerConnection.iceGatheringState === 'complete') {
        resolve();
        return;
      }
      let timer = null;
      let hasRelayCandidate = false;

      const onIceCandidate = (e) => {
        if (e.candidate && (e.candidate.type === 'relay' || e.candidate.candidate?.includes('typ relay'))) {
          hasRelayCandidate = true;
        }
      };

      const onGather = () => {
        if (this.peerConnection.iceGatheringState === 'complete') {
          cleanup();
          resolve();
        }
      };

      const cleanup = () => {
        if (timer) clearTimeout(timer);
        this.peerConnection.removeEventListener('icegatheringstatechange', onGather);
        this.peerConnection.removeEventListener('icecandidate', onIceCandidate);
      };

      this.peerConnection.addEventListener('icegatheringstatechange', onGather);
      this.peerConnection.addEventListener('icecandidate', onIceCandidate);

      // Allow up to 2000ms for TURN relay over TCP candidates to gather on restrictive Wi-Fi
      timer = setTimeout(() => {
        cleanup();
        resolve();
      }, 2000);
    });

    const finalOfferSdp = normalizeSdp(this.peerConnection.localDescription?.sdp || offer.sdp);
    const sdpBytes = new TextEncoder().encode(finalOfferSdp).length;
    const cseqNumber = ++this.cseq;

    if (!this.contactHost) {
      this.contactHost = `${this.generateId(8)}.invalid`;
    }
    const contactHost = this.contactHost;

    this.activeCall = {
      callSid,
      caller: normalizedDest,
      destinationNumber: normalizedDest,
      fromHeader: `<sip:${username}@${domain}>;tag=${fromTag}`,
      toHeader: `<sip:${normalizedDest}@${domain}>`,
      fromTag,
      toTag: null,
      branch,
      contactHost,
      contactHeader: null,
      recordRoute: null,
      cseq: cseqNumber,
      sdpOffer: finalOfferSdp,
      inboxId: meta.inboxId,
      contactId: meta.contactId,
      conversationId: meta.conversationId,
      callDirection: 'outbound',
      state: 'initiating',
      answered: false,
      startTime: Date.now(),
    };

    // Robust Caller ID Resolution & Normalization:
    // 1. Try meta.callerId
    // 2. Try credentials default callerId
    // 3. Extract any digit sequence from meta.callerId if it contained labels like "Voice Call (42-34509952)"
    const rawCli = meta.callerId || this.credentials?.callerId || '';
    let cleanCli = String(rawCli).replace(/[^\d+]/g, '');
    if (!cleanCli && typeof rawCli === 'string') {
      const match = rawCli.match(/\d[\d-]+/);
      if (match) cleanCli = match[0].replace(/[^\d+]/g, '');
    }

    let extraHeaders = '';
    if (cleanCli) {
      extraHeaders += `X-Omni-Caller-ID: ${cleanCli}\r\n`;
    }
    if (meta.inboxId) {
      extraHeaders += `X-Omni-Inbox-Id: ${meta.inboxId}\r\n`;
    }
    const resolvedAccountId = meta.accountId || this.credentials?.accountId;
    if (resolvedAccountId) {
      extraHeaders += `X-Omni-Account-Id: ${resolvedAccountId}\r\n`;
    }

    const invite =
      `INVITE sip:${normalizedDest}@${domain} SIP/2.0\r\n` +
      `Via: SIP/2.0/WSS ${contactHost};branch=${branch};rport\r\n` +
      `Max-Forwards: 70\r\n` +
      `From: <sip:${username}@${domain}>;tag=${fromTag}\r\n` +
      `To: <sip:${normalizedDest}@${domain}>\r\n` +
      `Call-ID: ${callSid}\r\n` +
      `CSeq: ${cseqNumber} INVITE\r\n` +
      `Contact: <sip:${username}@${contactHost};transport=ws>\r\n` +
      extraHeaders +
      `Allow: INVITE, ACK, CANCEL, BYE, NOTIFY, REFER, OPTIONS, INFO, SUBSCRIBE\r\n` +
      `Supported: replaces\r\n` +
      `User-Agent: omni-WebRTC/1.0\r\n` +
      `Content-Type: application/sdp\r\n` +
      `Content-Length: ${sdpBytes}\r\n\r\n` +
      finalOfferSdp;


    console.log('FreeSWITCH: Sending outbound SIP INVITE:\n', invite);
    this.ws.send(invite);

    return callSid;
  }

  handleHangup(sipMessage) {
    if (sipMessage && sipMessage.startsWith('BYE')) {
      this.sendSipResponse(200, 'OK', '', sipMessage);
    }
    this.endClientCall(false);
  }

  setMuted(shouldMute) {
    this.isMuted = !!shouldMute;
    console.log(`FreeSWITCH: setMuted(${this.isMuted})`);
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = !this.isMuted;
      });
    }
    if (this.peerConnection) {
      this.peerConnection.getSenders().forEach((sender) => {
        if (sender.track && sender.track.kind === 'audio') {
          sender.track.enabled = !this.isMuted;
        }
      });
    }
    return this.isMuted;
  }

  endClientCall(sendBye = true) {
    this.isMuted = false;
    if (!this.activeCall && !this.peerConnection && !this.localStream) {
      return;
    }

    const currentCall = this.activeCall;
    if (currentCall) {
      this.lastEndedCall = currentCall;
    }
    this.activeCall = null;

    if (this.peerConnection) {
      try {
        this.peerConnection.close();
      } catch (e) {}
      this.peerConnection = null;
    }
    if (this.localStream) {
      try {
        this.localStream.getTracks().forEach((track) => {
          track.enabled = false;
          track.stop();
        });
      } catch (e) {}
      this.localStream = null;
    }
    if (this.remoteAudioElement) {
      try {
        this.remoteAudioElement.pause();
        this.remoteAudioElement.srcObject = null;
      } catch (e) {}
    }

    if (sendBye && currentCall && this.ws && this.ws.readyState === WebSocket.OPEN) {
      const username = this.credentials?.username || currentCall?.fromHeader?.match(/<sip:([^@>]+)/)?.[1] || 'agent';
      const domain = this.credentials?.domain || 'media.justcreo.com';
      const dest = currentCall.destinationNumber || currentCall.caller;
      const contactHost = currentCall.contactHost || `${this.generateId(8)}.invalid`;

      if (currentCall.callDirection === 'outbound' && !currentCall.answered) {
        // Unanswered ringing outbound call: RFC 3261 requires CANCEL matching the INVITE transaction
        const routeHeader = currentCall.recordRoute ? `Route: ${currentCall.recordRoute}\r\n` : '';
        const cancel =
          `CANCEL sip:${dest}@${domain} SIP/2.0\r\n` +
          `Via: SIP/2.0/WSS ${contactHost};branch=${currentCall.branch};rport\r\n` +
          `Max-Forwards: 70\r\n` +
          routeHeader +
          `From: <sip:${username}@${domain}>;tag=${currentCall.fromTag}\r\n` +
          `To: <sip:${dest}@${domain}>\r\n` +
          `Call-ID: ${currentCall.callSid}\r\n` +
          `CSeq: ${currentCall.cseq} CANCEL\r\n` +
          `Contact: <sip:${username}@${contactHost};transport=ws>\r\n` +
          `User-Agent: omni-WebRTC/1.0\r\n` +
          `Content-Length: 0\r\n\r\n`;

        console.log('FreeSWITCH: Sending SIP CANCEL to terminate ringing outbound call:\n', cancel);
        this.ws.send(cancel);
      } else {
        // Answered outbound call or inbound call: send BYE
        const byeBranch = `z9hG4bK${this.generateId(12)}`;
        const cseqNumber = ++this.cseq;
        const routeHeader = currentCall.recordRoute ? `Route: ${currentCall.recordRoute}\r\n` : '';

        let toHeaderFormatted = currentCall.toHeader || `<sip:${dest}@${domain}>`;
        if (currentCall.toTag && !toHeaderFormatted.includes('tag=')) {
          toHeaderFormatted = `${toHeaderFormatted};tag=${currentCall.toTag}`;
        }

        let fromHeaderFormatted = currentCall.fromHeader || `<sip:${username}@${domain}>`;
        if (currentCall.fromTag && !fromHeaderFormatted.includes('tag=')) {
          fromHeaderFormatted = `${fromHeaderFormatted};tag=${currentCall.fromTag}`;
        }

        let byeRequestUri = `sip:${dest}@${domain}`;
        if (currentCall.contactHeader) {
          const contactMatch = currentCall.contactHeader.match(/<([^>]+)>/);
          if (contactMatch) {
            byeRequestUri = contactMatch[1];
          }
        }

        const bye =
          `BYE ${byeRequestUri} SIP/2.0\r\n` +
          `Via: SIP/2.0/WSS ${contactHost};branch=${byeBranch};rport\r\n` +
          `Max-Forwards: 70\r\n` +
          routeHeader +
          (currentCall.callDirection === 'outbound'
            ? `From: ${fromHeaderFormatted}\r\nTo: ${toHeaderFormatted}\r\n`
            : `From: <sip:${username}@${domain}>;tag=${currentCall.toTag}\r\nTo: ${currentCall.fromHeader || `<sip:${currentCall.caller}>`}\r\n`) +
          `Call-ID: ${currentCall.callSid}\r\n` +
          `CSeq: ${cseqNumber} BYE\r\n` +
          `Contact: <sip:${username}@${contactHost};transport=ws>\r\n` +
          `User-Agent: omni-WebRTC/1.0\r\n` +
          `Content-Length: 0\r\n\r\n`;

        console.log('FreeSWITCH: Sending SIP BYE to terminate call on FreeSWITCH:\n', bye);
        this.ws.send(bye);
      }
    }

    this.dispatchEvent(createCallDisconnectedEvent());
  }

  get hasActiveConnection() {
    return !!this.activeCall && !!this.peerConnection;
  }

  generateId(len = 16) {
    const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
    let str = '';
    for (let i = 0; i < len; i += 1) {
      str += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return str;
  }
}

export default new FreeswitchVoiceClient();
